/**
 * HealthPulse — single bento card with a 2x2 grid of business health tiles.
 *
 * Apple Health-inspired: each metric has its own gradient identity, big
 * number, contextual sub-line. Click any tile to navigate to that area.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Wrench, Truck, Trophy, Activity } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useOfficeDashboard } from '@/hooks/useOfficeDashboard';
import { FINANCIAL_MASK, shouldMaskFinancials } from '@/utils/financialVisibility';

type Health = 'good' | 'watch' | 'act' | 'neutral';

interface PulseTile {
  label: string;
  Icon: LucideIcon;
  health: Health;
  primary: string;
  secondary?: string;
  to: string;
  /** Tile gradient identity */
  gradient: string;
  iconGradient: string;
  ringColor: string;
}

/**
 * Compact dashboard money ("$1.2M", "$45k"). Keeps its own short form rather
 * than routing through utils/pricing — a full "$1,234,567.00" doesn't fit the
 * tile — so it checks the financial-visibility gate itself.
 */
function formatCurrency(n: number): string {
  if (shouldMaskFinancials()) return FINANCIAL_MASK;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

const ACT_DOT = 'bg-red-400';
const WATCH_DOT = 'bg-amber-400';
const GOOD_DOT = 'bg-emerald-400';
const NEUTRAL_DOT = 'bg-slate-400/40';

function dotFor(h: Health): string {
  return h === 'act' ? ACT_DOT : h === 'watch' ? WATCH_DOT : h === 'good' ? GOOD_DOT : NEUTRAL_DOT;
}

export function HealthPulse() {
  const navigate = useNavigate();
  const { stats, loading } = useOfficeDashboard();

  const tiles = useMemo<PulseTile[]>(() => {
    const pipelineHealth: Health = stats.attentionDeals > 5 ? 'watch' : 'good';
    const faultsHealth: Health = stats.criticalFaults > 0 ? 'act' : stats.openFaults > 3 ? 'watch' : 'good';

    return [
      {
        label: 'Pipeline',
        Icon: TrendingUp,
        health: pipelineHealth,
        primary: formatCurrency(stats.pipelineValue),
        secondary: `${stats.activeDeals} active`,
        to: '/home/deals',
        gradient: 'bg-gradient-to-br from-emerald-500/[0.10] via-emerald-500/[0.04] to-transparent',
        iconGradient: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
        ringColor: 'ring-emerald-400/20',
      },
      {
        label: 'Open faults',
        Icon: Wrench,
        health: faultsHealth,
        primary: String(stats.openFaults),
        secondary: stats.criticalFaults > 0 ? `${stats.criticalFaults} critical` : 'all calm',
        to: '/home/faults',
        gradient:
          faultsHealth === 'act'
            ? 'bg-gradient-to-br from-red-500/[0.12] via-red-500/[0.04] to-transparent'
            : 'bg-gradient-to-br from-rose-500/[0.06] via-rose-500/[0.02] to-transparent',
        iconGradient:
          faultsHealth === 'act'
            ? 'bg-gradient-to-br from-red-400 to-rose-600'
            : 'bg-gradient-to-br from-rose-400 to-rose-600',
        ringColor: faultsHealth === 'act' ? 'ring-red-400/30' : 'ring-rose-400/20',
      },
      {
        label: 'Today in field',
        Icon: Truck,
        health: 'neutral',
        primary: String(stats.todayJobs),
        secondary: `${stats.tomorrowJobs} tomorrow`,
        to: '/home/schedule',
        gradient: 'bg-gradient-to-br from-blue-500/[0.08] via-blue-500/[0.03] to-transparent',
        iconGradient: 'bg-gradient-to-br from-blue-400 to-indigo-600',
        ringColor: 'ring-blue-400/20',
      },
      {
        label: 'Won this week',
        Icon: Trophy,
        health: stats.wonDeals > 0 ? 'good' : 'neutral',
        primary: String(stats.wonDeals),
        secondary: 'deals signed',
        to: '/home/deals',
        gradient: 'bg-gradient-to-br from-amber-500/[0.10] via-amber-500/[0.04] to-transparent',
        iconGradient: 'bg-gradient-to-br from-amber-400 to-yellow-500',
        ringColor: 'ring-amber-400/25',
      },
    ];
  }, [stats]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.35 }}
      className="h-full rounded-3xl border border-theme bg-surface-card backdrop-blur-xl shadow-[0_8px_32px_-12px_rgba(0,0,0,0.4)] overflow-hidden flex flex-col"
    >
      {/* Header */}
      <header className="px-5 py-4 border-b border-theme-soft flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
          <Activity className="w-4 h-4 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <h3 className="text-base font-bold text-theme-primary leading-tight">Pulse</h3>
          <p className="text-[11px] text-theme-muted leading-tight mt-0.5">Business health at a glance</p>
        </div>
      </header>

      {/* 2x2 bento mini-tile grid */}
      <div className="flex-1 grid grid-cols-2 gap-3 p-4">
        {loading && stats.activeDeals === 0 && stats.openFaults === 0 && stats.todayJobs === 0 && stats.wonDeals === 0 ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="relative rounded-2xl bg-surface-raised/40 ring-1 ring-theme-soft p-4 animate-pulse"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-surface-hover" />
                  <div className="w-1.5 h-1.5 rounded-full bg-surface-hover" />
                </div>
                <div className="h-7 bg-surface-hover rounded w-2/3 mb-2" />
                <div className="h-2.5 bg-surface-hover/70 rounded w-1/2" />
              </div>
            ))}
          </>
        ) : tiles.map((tile, i) => {
          const Icon = tile.Icon;
          return (
            <motion.button
              key={tile.label}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25 + i * 0.04, type: 'spring', stiffness: 280, damping: 24 }}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(tile.to)}
              className={`relative text-left rounded-2xl ${tile.gradient} ring-1 ${tile.ringColor} p-4 group overflow-hidden transition-shadow hover:shadow-lg`}
            >
              {/* Top inner highlight — Apple glass */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />

              <div className="flex items-start justify-between mb-3">
                <div
                  className={`w-9 h-9 rounded-xl ${tile.iconGradient} flex items-center justify-center shadow-md`}
                >
                  <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                {tile.health !== 'neutral' && (
                  <motion.span
                    className={`w-1.5 h-1.5 rounded-full ${dotFor(tile.health)} shrink-0 mt-1`}
                    animate={tile.health === 'act' ? { scale: [1, 1.4, 1] } : undefined}
                    transition={{ duration: 1.4, repeat: Infinity }}
                  />
                )}
              </div>

              <div className="text-2xl font-light text-theme-primary leading-none mb-1.5 tracking-tight">
                {tile.primary}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium text-theme-muted truncate">{tile.label}</span>
                {tile.secondary && (
                  <span className="text-[10px] text-theme-muted/70 shrink-0">{tile.secondary}</span>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.section>
  );
}
